'use client';

/**
 * Client-side Logger using Pino
 * For use in client components and browser contexts
 */
import pino from 'pino';

const logLevel = process.env.NEXT_PUBLIC_LOG_LEVEL || 'info';

// Create browser-compatible logger
export const logger = pino({
  level: logLevel,
  browser: {
    asObject: true,
    serialize: true,
    write: {
      // Send logs to console in a formatted way
      info: (o: any) => {
        console.info(formatLog('INFO', o));
      },
      warn: (o: any) => {
        //console.warn(formatLog('WARN', o));
      },
      error: (o: any) => {
        console.error(formatLog('ERROR', o));
      },
      debug: (o: any) => {
        console.debug(formatLog('DEBUG', o));
      },
      trace: (o: any) => {
        console.trace(formatLog('TRACE', o));
      },
    },
  },
});

function formatLog(level: string, obj: any) {
  const { msg, name, time, ...rest } = obj;
  const timestamp = time ? new Date(time).toISOString() : new Date().toISOString();
  const context = name ? `[${name}]` : '';

  // Serialize errors properly
  const serializedRest = { ...rest };
  if (serializedRest.error instanceof Error) {
    serializedRest.error = {
      message: serializedRest.error.message,
      name: serializedRest.error.name,
      stack: serializedRest.error.stack,
      ...serializedRest.error,
    };
  }

  const extra = Object.keys(serializedRest).length > 0 ? JSON.stringify(serializedRest) : '';

  return `${timestamp} ${level} ${context} ${msg} ${extra}`.trim();
}

/**
 * Create a child logger with additional context
 */
export function createLogger(name: string, context?: Record<string, any>) {
  return logger.child({ name, ...context });
}

/**
 * Common loggers for different parts of the application
 */
export const loggers = {
  component: createLogger('component'),
  theme: createLogger('theme'),
  api: createLogger('api'),
  auth: createLogger('auth'),
  navigation: createLogger('navigation'),
  performance: createLogger('performance'),
};

/**
 * Performance measurement utility
 */
export function measurePerformance(name: string) {
  const start = performance.now();

  return {
    end: () => {
      const duration = performance.now() - start;
      loggers.performance.info({ msg: `${name} completed`, duration: `${duration.toFixed(2)}ms` });
      return duration;
    },
  };
}
